import axios, { AxiosResponse } from 'axios'
import qs from 'qs'
import {
  Identity,
  ResourceTypes,
  SingleResourceResponse,
  MultiResourceResponse,
  VendorGroup,
  VendorGroupAttributes,
  OrgWhiteLabel,
  OrgWhiteLabelAttributes,
  Organization,
  Resource,
  OrganizationAttributes,
  BaseEntity,
  ResourceId,
  CreateIdentity,
} from '@vendorflow/types'
import { convertResourceToEntity } from '@vendorflow/common/lib/converters/JsonApi.converter'
import { AZIMUTH_API_URL, VERSION } from 'constants/Api.constants'
import { People } from '@vendorflow/components/lib/components/DataTable/Data'
import { getRelationship } from 'services/Azimuth.service'
import { AUTHORIZATION_SENTINEL } from 'features/auth/Auth.constants'
import { IdentityWithRels } from 'types/Azimuth.types'
export const ORGANIZATION_URL = `${AZIMUTH_API_URL}/${VERSION}/organizations`

export async function fetchManagedIdentitiesUsingGET(
  id: string,
  includeSubscriptions?: boolean
): Promise<AxiosResponse<MultiResourceResponse<Identity, ResourceTypes.IDENTITIES>>> {
  return axios.get(
    `${ORGANIZATION_URL}/${id}/managedIdentities${
      includeSubscriptions ? '?' + qs.stringify({ include: 'receivesSubscription' }) : ''
    }`,
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )
}

export async function createManagedIdentityUsingPOST(envyId: string, identity: CreateIdentity): Promise<Identity> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { type, ...attributes } = identity
  const result: AxiosResponse<SingleResourceResponse<Identity, ResourceTypes.IDENTITIES>> = await axios.request({
    method: 'POST',
    url: `${ORGANIZATION_URL}/${envyId}/managedIdentities`,
    data: { data: { attributes, type: ResourceTypes.IDENTITIES } },
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })

  return convertResourceToEntity(result.data.data)
}

export async function fetchOrgsVendorGroupsUsingGET(id: string): Promise<VendorGroup[]> {
  const result: AxiosResponse<MultiResourceResponse<VendorGroup, ResourceTypes.VENDOR_GROUPS>> = await axios.get(
    `${ORGANIZATION_URL}/${id}/vendorGroups`,
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )

  return result.data.data.map((item) => convertResourceToEntity(item))
}

export async function createVendorGroupForOrgUsingPOST(
  id: string,
  vendorGroup: VendorGroupAttributes
): Promise<VendorGroup> {
  const result: AxiosResponse<SingleResourceResponse<VendorGroup, ResourceTypes.VENDOR_GROUPS>> = await axios.request({
    method: 'POST',
    url: `${ORGANIZATION_URL}/${id}/vendorGroups`,
    data: vendorGroup,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })
  return convertResourceToEntity(result.data.data)
}

export async function fetchWhiteLabelForOrgUsingGET(id: string): Promise<OrgWhiteLabel> {
  const result: AxiosResponse<SingleResourceResponse<OrgWhiteLabel, ResourceTypes.ORG_WHITELABEL>> = await axios.get(
    `${ORGANIZATION_URL}/${id}/whitelabel`,
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )
  return convertResourceToEntity(result.data.data)
}

export async function updateWhiteLabelForOrgUsingPUT(
  id: string,
  whitelabel: OrgWhiteLabelAttributes
): Promise<OrgWhiteLabel> {
  const result: AxiosResponse<SingleResourceResponse<OrgWhiteLabel, ResourceTypes.ORG_WHITELABEL>> = await axios.put(
    `${ORGANIZATION_URL}/${id}/whitelabel`,
    {
      data: {
        attributes: whitelabel,
      },
    },
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )

  return convertResourceToEntity(result.data.data)
}

export async function updateOrganizationUsingPUT(org: Organization): Promise<Organization> {
  const result: AxiosResponse<SingleResourceResponse<Organization, ResourceTypes.ORGANIZATIONS>> = await axios.put(
    `${ORGANIZATION_URL}/${org.id}`,
    {
      name: org.name,
      mcNumber: org.mcNumber,
    },
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )

  return convertResourceToEntity(result.data.data)
}

export async function searchForManagedIdentitiesAndVendorGroupsUsingGET(
  id: string,
  prefix: string
): Promise<(Identity | VendorGroup)[]> {
  const result: AxiosResponse<MultiResourceResponse<any, any>> = await axios.request({
    method: 'GET',
    url: `${ORGANIZATION_URL}/${id}/vendors?${qs.stringify({ prefix })}`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })

  return result.data.data.map((item) => convertResourceToEntity(item))
}

export async function fetchOrganizationUsingGET(id: string): Promise<Organization> {
  const result: AxiosResponse<SingleResourceResponse<any, ResourceTypes.ORGANIZATIONS>> = await axios.get(
    `${ORGANIZATION_URL}/${id}`,
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )
  return convertResourceToEntity(result.data.data)
}

export async function deleteOrganizationUsingDELETE(id: string): Promise<void> {
  await axios.delete(`${ORGANIZATION_URL}/${id}`, {
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })
}

export async function fetchOrganizationsUsingGET(): Promise<Organization[]> {
  const result: AxiosResponse<MultiResourceResponse<Organization, ResourceTypes.ORGANIZATIONS>> = await axios.request({
    method: 'GET',
    url: ORGANIZATION_URL,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })
  return result.data.data.map((item) => convertResourceToEntity(item))
}

export async function fetchManagedOrgMembersUsingGET(id: string): Promise<Identity[]> {
  const result: AxiosResponse<SingleResourceResponse<Organization, ResourceTypes.ORGANIZATIONS>> = await axios.get(
    `${ORGANIZATION_URL}/${id}?includes=members`,
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )
  const included = (result.data?.included as Resource<Identity, ResourceTypes.IDENTITIES>[]) || []

  return included.map((item) => convertResourceToEntity(item))
}

export async function fetchOrgMembersUsingGET(id: string): Promise<IdentityWithRels[]> {
  const result: AxiosResponse<MultiResourceResponse<Identity, ResourceTypes.IDENTITIES>> = await axios.request({
    method: 'GET',
    url: `${ORGANIZATION_URL}/${id}/users`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })
  return result.data.data.map((item) => {
    const identity: IdentityWithRels = convertResourceToEntity(item)

    if (item.relationships?.administers) {
      identity.rels = {
        administers: getRelationship<ResourceTypes.ORGANIZATIONS>(item.relationships.administers).get(),
      }
    }

    return identity
  })
}

export async function createUserForOrgUsingPOST(id: string, identity: CreateIdentity): Promise<Identity> {
  const { firstName, lastName, displayName, emails } = identity
  const result: AxiosResponse<SingleResourceResponse<People, ResourceTypes.PEOPLE>> = await axios.request({
    method: 'POST',
    url: `${ORGANIZATION_URL}/${id}/users`,
    data: {
      data: {
        type: 'organizations--new-users',
        attributes: {
          identity: { firstName, lastName, displayName, emails },
          createCaptiveChannel: false,
          authentication: {
            createLogin: true,
            initialPassword: 'Welcome1',
          },
        },
      },
    },
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })
  const identityRel: BaseEntity<ResourceTypes.IDENTITIES> = getRelationship(
    result.data.data.relationships?.assumes
  ).getFirst()!

  return convertResourceToEntity(
    result.data.included!.find((item) => item.id === identityRel.id && item.type === identityRel.type)!
  )
}

/**
 * Managed Orgs
 */
export async function fetchManagedOrgsUsingGET(id: string): Promise<Organization[]> {
  const result: AxiosResponse<MultiResourceResponse<Organization, ResourceTypes.ORGANIZATIONS>> = await axios.get(
    `${ORGANIZATION_URL}/${id}/managedOrganizations`,
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )

  return result.data.data.map((item) => convertResourceToEntity(item))
}

export async function createManagedOrgUsingPOST(id: string, org: OrganizationAttributes): Promise<void> {
  await axios.post(`${ORGANIZATION_URL}/${id}/managedOrganizations`, org, {
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })
}

/**
 * Managed Members
 */
export async function addMembersToOrgUsingPOST(
  orgId: string,
  identities: BaseEntity<ResourceTypes.IDENTITIES>[]
): Promise<void> {
  await axios.post(
    `${ORGANIZATION_URL}/${orgId}/rel/members`,
    { data: identities },
    {
      headers: { Authorization: AUTHORIZATION_SENTINEL },
    }
  )
}

export async function removeMembersFromOrgUsingDELETE(
  orgId: string,
  identities: BaseEntity<ResourceTypes.IDENTITIES>[]
): Promise<void> {
  await axios({
    method: 'DELETE',
    url: `${ORGANIZATION_URL}/${orgId}/rel/members`,
    data: { data: identities },
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })
}

export async function searchAlicesByPhoneUsingGET(orgId: string, phone: string): Promise<Identity[]> {
  const result: AxiosResponse<MultiResourceResponse<Identity, ResourceTypes.IDENTITIES>> = await axios.request({
    method: 'GET',
    url: `${ORGANIZATION_URL}/${orgId}/vendors?${qs.stringify({ phone })}`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })

  return result.data.data.map((item) => convertResourceToEntity(item))
}

export async function fetchRootOrganizationUsingGET(envy: string) {
  const result: AxiosResponse<{ data: ResourceId<ResourceTypes.ORGANIZATIONS> }> = await axios.request({
    method: 'GET',
    url: `${ORGANIZATION_URL}/${envy}/rel/root`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })

  return result.data.data.id
}

export async function fetchEnviesWithSameRootUsingGET(envy: string): Promise<Organization[]> {
  const result: AxiosResponse<MultiResourceResponse<Organization, ResourceTypes.ORGANIZATIONS>> = await axios.request({
    method: 'GET',
    url: `${ORGANIZATION_URL}?${qs.stringify({ filter: { sameRoot: envy } })}`,
    headers: { Authorization: AUTHORIZATION_SENTINEL },
  })

  return result.data.data.map((item) => convertResourceToEntity(item))
}
